/**
 * The master pane's toolbar: the search field, and the square that opens the filters.
 *
 * ── The search box is the app's one field, in its compact cut ────────────────────────────────
 * `shared/ui/SearchField` at `size="compact"` — the ×0,8 geometry KANON draws in a 461px pane. The
 * placeholder is the SCOPE's, because the two lists are searched by different things: a template by
 * its name, a client's plan by the client or the plan. That is the canon's own copy, verbatim.
 *
 * ── The square is the popover's TRIGGER, which is what gives focus its way back ──────────────
 * Base UI returns focus to a `Popover.Trigger` when the panel closes, whether the trainer pressed
 * Escape, the ✕ or `Pokaż N`. The header's ember `+` is deliberately NOT a trigger — the band owns
 * its layout — and it pays for that with a ref threaded through the screen; there is no reason to
 * repeat that here.
 *
 * ── The dot means "something is on", and never how much ──────────────────────────────────────
 * One mark for any number of active facets, which is the canon's own `nActive ? 'block' : 'none'`.
 * How MANY is on each tab's own pill inside the panel, where the trainer can act on it. The dot is
 * decorative and the square's accessible name says the same thing in words, because a mark alone
 * never carries a state in this app.
 */

import { Popover } from '@base-ui/react/popover';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchField } from '~/shared/ui/SearchField';

import {
  activeFacetCount,
  type ClientFacetOption,
  type PlanLibraryFilter,
} from '../model/planLibraryFilter';
import type { PlanScope } from '../model/planScope';
import { FilterPanel } from './FilterPanel';
import { FilterIcon } from './PlanLibraryIcons';
import square from '~/shared/ui/filterSquare.module.css';
import styles from './ListToolbar.module.css';

interface ListToolbarProps {
  readonly scope: PlanScope;
  readonly filter: PlanLibraryFilter;
  readonly onFilterChange: (filter: PlanLibraryFilter) => void;
  readonly clientOptions: readonly ClientFacetOption[];
  /** How many rows the list is showing — the panel's `Pokaż N`. */
  readonly matchCount: number;
  /** Dead while there is nothing to search — a pending or failed read. */
  readonly disabled: boolean;
  /** The list card the panel hangs off. The SCREEN owns it, because the screen owns the card. */
  readonly anchor: RefObject<HTMLElement | null>;
  /** The search `<input>`, so the screen can route a bare printable key into it. */
  readonly searchRef: RefObject<HTMLInputElement | null>;
}

const PLACEHOLDER_KEYS: Readonly<Record<PlanScope, string>> = {
  templates: 'web:planLibrary.searchPlaceholder',
  clients: 'web:planLibrary.searchClientPlansPlaceholder',
};

export function ListToolbar({
  scope,
  filter,
  onFilterChange,
  clientOptions,
  matchCount,
  disabled,
  anchor,
  searchRef,
}: ListToolbarProps) {
  const { t } = useTranslation();
  const placeholder = t(PLACEHOLDER_KEYS[scope]);
  const facets = activeFacetCount(filter, scope);

  return (
    <div className={styles.toolbar}>
      <SearchField
        inputRef={searchRef}
        size="compact"
        value={filter.search}
        onChange={(search) => {
          onFilterChange({ ...filter, search });
        }}
        // The field's accessible name is the placeholder's sentence: a placeholder disappears on the
        // first keystroke, so it cannot be the name, and the two say the same thing for a reason.
        label={placeholder}
        placeholder={placeholder}
        clearLabel={t('web:planLibrary.clearSearch')}
        disabled={disabled}
      />

      <FilterPanel
        scope={scope}
        filter={filter}
        onFilterChange={onFilterChange}
        clientOptions={clientOptions}
        matchCount={matchCount}
        anchor={anchor}
      >
        {/* An icon-only control, so it carries its whole name in `aria-label` and its glyph is
          * `aria-hidden`. `title` as well, because there is no visible text to say what it does
          * under a pointer. The name gains a second sentence once a facet is on, so the dot is
          * never the only thing saying so. */}
        <Popover.Trigger
          className={square.square}
          title={t('web:planLibrary.filters.open')}
          aria-label={t(
            facets === 0 ? 'web:planLibrary.filters.open' : 'web:planLibrary.filters.active',
          )}
        >
          <FilterIcon className={square.icon} />
          {facets > 0 && <span className={square.dot} aria-hidden />}
        </Popover.Trigger>
      </FilterPanel>
    </div>
  );
}
